/**
 * Template Review Traveller Selectors
 *
 * Memoized selectors for accessing flight review traveller state.
 *
 * Architecture:
 * - Template data (immutable) is stored in chat/template slice and passed via props
 * - This file contains selectors for:
 *   1. Mutable UI state stored in flights/templateReviewTraveller slice
 *   2. Derived data from the active template in chat/template slice
 */
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { FlightsReviewTravellersTemplateData } from '@contracts';
import { buildPrebookPayload } from '@/features/flights/flight-listing/components/flight-traveller/utils/travellerTransformers';

// ============================================
// Base Selectors
// ============================================

const selectTemplateReviewTravellerState = (state: RootState) =>
  state.flights.templateReviewTraveller;

/** Select UI state (mutable user interaction state) */
const selectUiState = createSelector(
  [selectTemplateReviewTravellerState],
  (reviewTraveller) => reviewTraveller.uiState,
);

/**
 * Select template data from chat/template slice
 * This is the source of truth for immutable template data
 */
const selectChatTemplateState = (state: RootState) => state.chat.template;

const selectActiveTemplate = createSelector(
  [selectChatTemplateState],
  (template) => template.activeTemplate,
);

const selectTemplateData = createSelector(
  [selectChatTemplateState],
  (template) => template.templateData,
);

/**
 * Select review traveller template data (only when active template is flights-review-travellers)
 */
const selectReviewTravellerTemplateData = createSelector(
  [selectActiveTemplate, selectTemplateData],
  (activeTemplate, templateData): FlightsReviewTravellersTemplateData | null => {
    if (activeTemplate === 'flights-review-travellers' && templateData) {
      return templateData as FlightsReviewTravellersTemplateData;
    }
    return null;
  },
);

// ============================================
// Template Data Selectors (from chat/template slice)
// These selectors derive UI-ready data from the active template
// ============================================

/** Select itinerary ID from template data */
export const selectItineraryId = createSelector(
  [selectReviewTravellerTemplateData],
  (templateData) => templateData?.itineraryId ?? null,
);

/**
 * Loading state - returns true if template data is not available
 * Used by FlightReview.tsx to show loading state
 */
export const selectTravellerFormLoading = createSelector(
  [selectReviewTravellerTemplateData],
  (templateData) => !templateData,
);

// ============================================
// UI State Selectors (Mutable)
// These selectors access user-editable state from flights/templateReviewTraveller slice
// ============================================

/** Select form validation status from UI state */
export const selectIsTravellerFormValidated = createSelector(
  [selectUiState],
  (uiState) => uiState.isTravellerFormValidated,
);

/** Select fare summary from UI state (updates on addon selection) */
export const selectFareSummary = createSelector([selectUiState], (uiState) => uiState.fareSummary);

/** Select addon loading state from UI state */
export const selectIsAddonLoading = createSelector(
  [selectUiState],
  (uiState) => uiState.isAddonLoading,
);

/** Select current popup from UI state */
export const selectCurrentPopup = createSelector(
  [selectUiState],
  (uiState) => uiState.currentPopup,
);

/** Select saved travellers from UI state (user-editable) */
export const selectSavedTravellers = createSelector(
  [selectUiState],
  (uiState) => uiState.savedTravellers,
);

/** Select saved contact details from UI state (user-editable) */
export const selectSavedContactDetails = createSelector(
  [selectUiState],
  (uiState) => uiState.savedContactDetails,
);

/** Select saved GST details from UI state (user-editable) */
export const selectSavedGstDetails = createSelector(
  [selectUiState],
  (uiState) => uiState.savedGstDetails,
);

/**
 * Select template stream version from UI state.
 * This version increments when the template is updated from chat stream.
 * Used by addon components (coupons, seats, meals, baggage) to trigger re-fetch
 * when the backend sends updated template data (e.g., after applying a coupon via chat).
 */
export const selectTemplateStreamVersion = createSelector(
  [selectUiState],
  (uiState) => uiState.templateStreamVersion,
);

/** Select validated traveller IDs (travellers whose isOpenForm modal was already saved) */
export const selectValidatedTravellerIds = createSelector(
  [selectUiState],
  (uiState) => uiState.validatedTravellerIds,
);

// ============================================
// Computed Prebook Payload Selector
// ============================================

/**
 * Compute the prebook payload from saved travellers, contact details, and GST details.
 * This is used for the prebook API call instead of storing validatedTravellerData in Redux.
 *
 * Returns null if no travellers are selected (pre-selected).
 */
export const selectPrebookPayload = createSelector(
  [selectSavedTravellers, selectSavedContactDetails, selectSavedGstDetails, selectItineraryId],
  (savedTravellers, savedContactDetails, savedGstDetails, itineraryId) => {
    return buildPrebookPayload(savedTravellers, savedContactDetails, savedGstDetails, itineraryId);
  },
);
